import React, { useState, useRef, useEffect } from 'react';
import * as d3 from 'd3';
import { useTheme } from '../../../contexts/ThemeContext';

interface Props {
  nodes: any[];
}

const statusColors: Record<string, string> = {
  fresh: 'bg-green-500',
  ghost: 'bg-gray-600',
  stale: 'bg-amber-500',
  locked: 'bg-blue-500',
  pending: 'bg-yellow-500',
  hydrating: 'bg-purple-500',
  // Uppercase fallbacks in case some data paths return uppercase
  FRESH: 'bg-green-500',
  GHOST: 'bg-gray-600',
  STALE: 'bg-amber-500',
  LOCKED: 'bg-blue-500',
  PENDING: 'bg-yellow-500',
  HYDRATING: 'bg-purple-500',
};

type ViewMode = 'treemap' | 'grid' | 'tree';

const statusFillColors: Record<string, string> = {
  fresh: '#22c55e', ghost: '#4b5563', stale: '#f59e0b', locked: '#3b82f6',
  pending: '#eab308', hydrating: '#8b5cf6',
  FRESH: '#22c55e', GHOST: '#4b5563', STALE: '#f59e0b', LOCKED: '#3b82f6',
};

const TreemapView: React.FC<{ nodes: any[]; darkMode: boolean; containerRef: React.RefObject<HTMLDivElement> }> = ({ nodes, darkMode, containerRef }) => {
  useEffect(() => {
    if (!containerRef.current || nodes.length === 0) return;
    const el = containerRef.current;
    d3.select(el).selectAll('*').remove();

    const width = el.clientWidth;
    const height = 320;

    // Build hierarchy: root -> directories -> files
    const dirMap = new Map<string, any[]>();
    nodes.forEach(n => {
      const parts = (n.path || '').split('/');
      const dir = parts.length > 1 ? parts.slice(0, -1).join('/') : '/';
      if (!dirMap.has(dir)) dirMap.set(dir, []);
      dirMap.get(dir)!.push(n);
    });

    const hierarchyData = {
      name: 'root',
      children: Array.from(dirMap.entries()).map(([dir, children]) => ({
        name: dir,
        children: children.map(n => ({
          name: n.name || n.path,
          value: 1,
          status: (n.status || 'ghost').toLowerCase(),
          path: n.path,
        })),
      })),
    };

    const root = d3.hierarchy(hierarchyData)
      .sum((d: any) => d.value || 0)
      .sort((a, b) => (b.value || 0) - (a.value || 0));

    d3.treemap<any>()
      .size([width, height])
      .paddingOuter(3)
      .paddingInner(2)
      .round(true)(root);

    const svg = d3.select(el).append('svg')
      .attr('width', width)
      .attr('height', height);

    const leaves = root.leaves();

    leaves.forEach((leaf: any) => {
      const g = svg.append('g').attr('transform', `translate(${leaf.x0},${leaf.y0})`);
      const w = leaf.x1 - leaf.x0;
      const h = leaf.y1 - leaf.y0;

      g.append('rect')
        .attr('width', w)
        .attr('height', h)
        .attr('rx', 3)
        .attr('fill', statusFillColors[leaf.data.status] || '#4b5563')
        .attr('opacity', 0.85)
        .attr('cursor', 'pointer')
        .on('mouseenter', function() { d3.select(this).attr('opacity', 1).attr('stroke', '#fff').attr('stroke-width', 1); })
        .on('mouseleave', function() { d3.select(this).attr('opacity', 0.85).attr('stroke', null as any); })
        .append('title')
        .text(`${leaf.data.path || leaf.data.name} (${leaf.data.status})`);

      if (w > 40 && h > 14) {
        g.append('text')
          .attr('x', 3)
          .attr('y', 11)
          .attr('font-size', 9)
          .attr('fill', '#fff')
          .attr('pointer-events', 'none')
          .text(leaf.data.name.length > w / 6 ? leaf.data.name.slice(0, Math.floor(w / 6)) + '..' : leaf.data.name);
      }
    });
  }, [nodes, darkMode, containerRef]);

  if (nodes.length === 0) return null;

  return <div ref={containerRef} className="w-full overflow-hidden" />
};

const NodeStatusMap: React.FC<Props> = ({ nodes }) => {
  const { darkMode } = useTheme();
  const [view, setView] = useState<ViewMode>('treemap');
  const treemapRef = useRef<HTMLDivElement>(null);
  const [filterStatus, setFilterStatus] = useState<string | null>(null);

  const filtered = filterStatus ? nodes.filter(n => n.status === filterStatus) : nodes;

  // Group by top-level directory
  const groups = new Map<string, any[]>();
  filtered.forEach(n => {
    const parts = (n.path || '').split('/');
    const dir = parts.length > 1 ? parts[0] : '/';
    if (!groups.has(dir)) groups.set(dir, []);
    groups.get(dir)!.push(n);
  });

  // Status counts
  const counts: Record<string, number> = {};
  nodes.forEach(n => { counts[n.status] = (counts[n.status] || 0) + 1; });

  return (
    <div>
      {/* Filter + View toggle */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2">
          {Object.entries(counts).map(([status, count]) => (
            <button
              key={status}
              onClick={() => setFilterStatus(filterStatus === status ? null : status)}
              className={`px-2 py-1 text-xs rounded flex items-center gap-1 transition-colors ${
                filterStatus === status
                  ? 'ring-2 ring-purple-500'
                  : darkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600'
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${statusColors[status] || 'bg-gray-500'}`} />
              {status}: {count}
            </button>
          ))}
        </div>
        <div className="flex gap-1">
          {(['treemap', 'grid', 'tree'] as ViewMode[]).map(v => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-2 py-1 text-xs rounded ${
                view === v
                  ? 'bg-purple-600 text-white'
                  : darkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600'
              }`}
            >
              {v}
            </button>
          ))}
        </div>
      </div>

      {view === 'treemap' ? (
        /* D3 Treemap */
        <TreemapView nodes={filtered} darkMode={darkMode} containerRef={treemapRef} />
      ) : view === 'grid' ? (
        /* Grid Mosaic */
        <div className="space-y-4">
          {Array.from(groups.entries()).map(([dir, dirNodes]) => (
            <div key={dir}>
              <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{dir}/</p>
              <div className="flex flex-wrap gap-1">
                {dirNodes.map(n => (
                  <div
                    key={n.id}
                    className={`w-4 h-4 rounded-sm ${statusColors[n.status] || 'bg-gray-500'} cursor-pointer transition-transform hover:scale-150`}
                    title={`${n.path} (${n.status})`}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Tree View — proper nested directory tree */
        <div className={`rounded-lg border p-3 font-mono text-xs max-h-[500px] overflow-y-auto ${darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
          {(() => {
            // Build nested tree from flat paths
            const tree: Record<string, any> = {};
            filtered.forEach(n => {
              const parts = (n.path || n.name || '').split('/');
              let cursor = tree;
              parts.forEach((part: string, i: number) => {
                if (i === parts.length - 1) {
                  cursor[part] = { __leaf: n };
                } else {
                  if (!cursor[part] || cursor[part].__leaf) cursor[part] = {};
                  cursor = cursor[part];
                }
              });
            });

            const renderTree = (obj: Record<string, any>, depth: number = 0): React.ReactNode[] => {
              return Object.entries(obj)
                .sort(([a, av], [b, bv]) => {
                  const aDir = !av.__leaf;
                  const bDir = !bv.__leaf;
                  if (aDir !== bDir) return aDir ? -1 : 1;
                  return a.localeCompare(b);
                })
                .map(([name, value]) => {
                  if (value.__leaf) {
                    const n = value.__leaf;
                    const status = (n.status || 'ghost').toLowerCase();
                    return (
                      <div key={n.path || name} className="flex items-center gap-1.5 py-px" style={{ paddingLeft: depth * 16 }}>
                        <span className={`w-2 h-2 rounded-full shrink-0 ${statusColors[status] || 'bg-gray-500'}`} />
                        <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>{name}</span>
                        <span className={darkMode ? 'text-gray-600' : 'text-gray-400'}>{status}</span>
                      </div>
                    );
                  }
                  // Directory — count children recursively
                  const files: any[] = [];
                  const collect = (o: Record<string, any>) => {
                    Object.values(o).forEach((v: any) => {
                      if (v.__leaf) files.push(v.__leaf);
                      else collect(v);
                    });
                  };
                  collect(value);
                  const freshC = files.filter(f => (f.status || '').toLowerCase() === 'fresh').length;

                  return (
                    <React.Fragment key={`${depth}-${name}`}>
                      <div className="flex items-center gap-1.5 py-px" style={{ paddingLeft: depth * 16 }}>
                        <span className={darkMode ? 'text-cyan-400' : 'text-cyan-600'}>{name}/</span>
                        <span className={darkMode ? 'text-gray-600' : 'text-gray-400'}>
                          {files.length}{freshC > 0 ? ` (${freshC} fresh)` : ''}
                        </span>
                      </div>
                      {renderTree(value, depth + 1)}
                    </React.Fragment>
                  );
                });
            };

            const items = renderTree(tree);
            return items.length > 0 ? items : (
              <p className={darkMode ? 'text-gray-500' : 'text-gray-400'}>No nodes to display</p>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default NodeStatusMap;
